import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  TrendingUp,
  CalendarToday,
  AttachMoney,
  Warning,
  Edit,
  Delete,
  ViewList,
  Pause,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, addDays, isAfter, isBefore } from 'date-fns';
import { useQuery, useQueryClient } from 'react-query';
import { supabase, Subscription } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { calculateTotalMonthlySpending, formatCurrency, getMonthlyCostInCurrency } from '../utils/currency';
import EditSubscriptionModal from '../components/EditSubscriptionModal';
import toast from 'react-hot-toast';
import { getCategoryColor } from '../utils/categoryColors';
import { useUserPreferences } from '../hooks/useUserPreferences';
import ServiceLogo from '../components/ServiceLogo';
import { formatBillingCycleForDisplay } from '../utils/billingCycleUtils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getMainCurrency } = useUserPreferences(user?.id);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<Subscription | null>(null);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [selectedSubscriptionForMobile, setSelectedSubscriptionForMobile] = useState<Subscription | null>(null);

  // Show/hide inactive subscriptions
  const [showInactive, setShowInactive] = useState(false);

  const { data: subscriptions, isLoading } = useQuery(
    ['subscriptions', user?.id, showInactive],
    async () => {
      if (!user) return [];
      let query = supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id);
      
      if (!showInactive) {
        query = query.eq('is_active', true);
      }
      
      const { data, error } = await query.order('next_renewal_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!user,
    }
  );

  // Get user's main currency
  const userPreferredCurrency = getMainCurrency();
  
  const totalMonthlyCost = subscriptions ? 
    calculateTotalMonthlySpending(subscriptions, userPreferredCurrency) : 0;

  const upcomingPayments = subscriptions?.filter(sub => 
    isAfter(new Date(sub.next_renewal_date), new Date()) &&
    isBefore(new Date(sub.next_renewal_date), addDays(new Date(), 30))
  ).slice(0, 5) || [];

  const trialEndingSoon = subscriptions?.filter(sub => 
    sub.is_trial && 
    sub.trial_end_date &&
    isAfter(new Date(sub.trial_end_date), new Date()) &&
    isBefore(new Date(sub.trial_end_date), addDays(new Date(), 7))
  ) || [];



  const handleEditSubscription = (subscription: Subscription) => {
    console.log('handleEditSubscription called with:', subscription);
    setSelectedSubscription(subscription);
    setEditModalOpen(true);
    console.log('Edit modal state set to open');
  };

  const handleMobileSubscriptionClick = (subscription: Subscription) => {
    setSelectedSubscriptionForMobile(subscription);
    setMobileModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleDeleteSubscription = (subscription: Subscription) => {
    setSubscriptionToDelete(subscription);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!subscriptionToDelete) return;
    
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', subscriptionToDelete.id);
      
      if (error) throw error;
      
      // Refresh the subscriptions list using React Query
      queryClient.invalidateQueries(['subscriptions', user?.id]);
      toast.success('Subscription deleted successfully!');
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Failed to delete subscription');
    } finally {
      setDeleteDialogOpen(false);
      setSubscriptionToDelete(null);
    }
  };

  const handleToggleSubscription = async (subscription: Subscription) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ is_active: !subscription.is_active })
        .eq('id', subscription.id);
      
      if (error) throw error;
      
      // Refresh the subscriptions list using React Query
      queryClient.invalidateQueries(['subscriptions', user?.id]);
      toast.success(`Subscription ${subscription.is_active ? 'disabled' : 'enabled'} successfully!`);
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Failed to update subscription status');
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress 
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C00 50%, #32CD32 100%)',
            },
          }}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's an overview of your subscriptions.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Monthly Cost Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                                <Typography variant="h4" component="div">
                {formatCurrency(totalMonthlyCost, userPreferredCurrency)}
              </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Cost
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {subscriptions?.length || 0} active subscriptions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Subscriptions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {subscriptions?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Subscriptions
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Across {new Set(subscriptions?.map(s => s.category) || []).size} categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Payments */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {upcomingPayments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming Payments
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Next 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Payments List */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexDirection: { xs: 'row', md: 'row' } }}>
                  <Typography variant="h6" sx={{ textAlign: { xs: 'left', md: 'inherit' }, width: { xs: 'auto', md: 'auto' } }}>Upcoming Payments</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Inactive"
                    sx={{ 
                      mb: { xs: 0, md: 0 },
                      display: { xs: 'block', md: 'none' },
                      whiteSpace: 'nowrap'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', md: 'row' }, width: { xs: '100%', md: 'auto' } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    order: { xs: -1, md: 0 },
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'flex-end', md: 'flex-start' }
                  }}>
                    <Button
                      variant="outlined"
                      startIcon={<ViewList />}
                      onClick={() => navigate('/all-subscriptions')}
                      sx={{ mr: { xs: 0, md: 1 }, width: { xs: 'auto', md: 'auto' }, whiteSpace: 'nowrap' }}
                    >
                      View All
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => navigate('/add-subscription')}
                      sx={{ width: { xs: 'auto', md: 'auto' }, whiteSpace: 'nowrap' }}
                    >
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        Add Subscription
                      </Box>
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        Add New
                      </Box>
                    </Button>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Inactive"
                    sx={{ mb: { xs: 1, md: 0 }, display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}
                  />
                </Box>
              </Box>
              
              {upcomingPayments.length > 0 ? (
                <List>
                  {upcomingPayments.map((subscription) => (
                    <ListItem 
                      key={subscription.id} 
                      divider
                      sx={{
                        opacity: subscription.is_active ? 1 : 0.6,
                        backgroundColor: subscription.is_active ? 'transparent' : 'grey.50',
                        p: { xs: 2, md: 1 },
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: { xs: 1, md: 0 },
                        cursor: { xs: 'pointer', md: 'default' },
                        '&:hover': {
                          backgroundColor: { xs: 'action.hover', md: 'transparent' }
                        }
                      }}
                      onClick={() => {
                        if (window.innerWidth < 960) { // md breakpoint
                          handleMobileSubscriptionClick(subscription);
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                        <ServiceLogo
                          serviceName={subscription.name}
                          category={subscription.category}
                          size="large"
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                            <Typography 
                              variant="body1" 
                              fontWeight="medium"
                              sx={{ 
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                            >
                              {subscription.name}
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight="medium" 
                              sx={{ 
                                color: '#32CD32',
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                display: { xs: 'block', md: 'none' }
                              }}
                            >
                              {formatCurrency(subscription.cost, subscription.currency || 'USD')}
                            </Typography>
                            {!subscription.is_active && (
                              <Chip label="Inactive" size="small" color="default" sx={{ fontSize: { xs: '0.625rem', md: '0.75rem' } }} />
                            )}
                          </Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: '0.75rem', md: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {format(new Date(subscription.next_renewal_date), 'MMM dd, yyyy')} • {formatBillingCycleForDisplay(subscription.billing_cycle)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ 
                        display: { xs: 'none', md: 'flex' }, 
                        alignItems: 'center', 
                        gap: 1, 
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        width: { xs: '100%', md: 'auto' },
                        mt: { xs: 1, md: 0 }
                      }}>
                        <Typography variant="body1" fontWeight="medium" sx={{ color: '#32CD32' }}>
                          {formatCurrency(subscription.cost, subscription.currency || 'USD')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton 
                            size="small"
                            onClick={() => handleEditSubscription(subscription)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => handleToggleSubscription(subscription)}
                            title={subscription.is_active ? 'Deactivate subscription' : 'Activate subscription'}
                          >
                            <Pause />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => handleDeleteSubscription(subscription)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No upcoming payments in the next 30 days
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Design Box - Image */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 0, overflow: 'hidden', height: '400px' }}>
            <img 
              src="/images/dashboard-design.png" 
              alt="Dashboard Design"
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Card>
        </Grid>

        {/* Trial Ending Soon */}
        {trialEndingSoon.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Warning sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Typography variant="h6">Trial Ending Soon</Typography>
                </Box>
                
                <List>
                  {trialEndingSoon.map((subscription) => (
                    <ListItem key={subscription.id} divider>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <ServiceLogo
                          serviceName={subscription.name}
                          category={subscription.category}
                          size="large"
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {subscription.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Trial ends {format(new Date(subscription.trial_end_date!), 'MMM dd, yyyy')}
                          </Typography>
                        </Box>
                      </Box>
                      <ListItemSecondaryAction>
                        <Button size="small" color="warning" variant="outlined">
                          Cancel
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Category Breakdown */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Spending by Category
              </Typography>
              
              <Grid container spacing={2}>
                {Array.from(new Set(subscriptions?.map(s => s.category) || [])).map((category) => {
                  const categorySubs = subscriptions?.filter(s => s.category === category) || [];
                  const categoryCost = categorySubs.reduce((sum, sub) => {
                    return sum + getMonthlyCostInCurrency(sub, userPreferredCurrency);
                  }, 0);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={category}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                            {category}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(categoryCost, userPreferredCurrency)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(categoryCost / totalMonthlyCost) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getCategoryColor(category),
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {categorySubs.length} subscription{categorySubs.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Edit Subscription Modal */}
      <EditSubscriptionModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        subscription={selectedSubscription}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{subscriptionToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Subscription Modal */}
      <Dialog
        open={mobileModalOpen}
        onClose={() => setMobileModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 2, sm: 'auto' },
            width: { xs: 'calc(100% - 32px)', sm: 'auto' }
          }
        }}
      >
        {selectedSubscriptionForMobile && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ServiceLogo
                  serviceName={selectedSubscriptionForMobile.name}
                  category={selectedSubscriptionForMobile.category}
                  size="large"
                />
                <Box>
                  <Typography variant="h6" component="div">
                    {selectedSubscriptionForMobile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {selectedSubscriptionForMobile.category}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Price */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Cost
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#32CD32' }}>
                    {formatCurrency(selectedSubscriptionForMobile.cost, selectedSubscriptionForMobile.currency || 'USD')}
                  </Typography>
                </Box>

                {/* Billing Cycle */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Billing Cycle
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatBillingCycleForDisplay(selectedSubscriptionForMobile.billing_cycle)}
                  </Typography>
                </Box>

                {/* Next Renewal */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Next Renewal
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {format(new Date(selectedSubscriptionForMobile.next_renewal_date), 'MMM dd, yyyy')}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={selectedSubscriptionForMobile.is_active ? 'Active' : 'Inactive'} 
                    color={selectedSubscriptionForMobile.is_active ? 'success' : 'default'} 
                    size="small" 
                  />
                </Box>

                {/* Notes */}
                {selectedSubscriptionForMobile.notes && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: '60%', textAlign: 'right' }}>
                      {selectedSubscriptionForMobile.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setMobileModalOpen(false);
                  handleEditSubscription(selectedSubscriptionForMobile);
                }}
                sx={{ flex: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<Pause />}
                onClick={() => {
                  setMobileModalOpen(false);
                  handleToggleSubscription(selectedSubscriptionForMobile);
                }}
                sx={{ flex: 1 }}
              >
                {selectedSubscriptionForMobile.is_active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  setMobileModalOpen(false);
                  handleDeleteSubscription(selectedSubscriptionForMobile);
                }}
                sx={{ flex: 1 }}
              >
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default Dashboard; 